import { useEffect, useRef, useState } from 'react'
import { useInstrumentSearch } from '../hooks/useInstrumentSearch'
import { useRecentTickersStore } from '../stores/useRecentTickersStore'

interface Props {
  onSelect: (symbol: string) => void
  onClose: () => void
}

export function MobileSearchOverlay({ onSelect, onClose }: Props) {
  const [query, setQuery] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const recentTickers = useRecentTickersStore((s) => s.tickers)
  const addTicker = useRecentTickersStore((s) => s.add)
  const removeTicker = useRecentTickersStore((s) => s.remove)
  const { results, isLoading, isSyncing, error } = useInstrumentSearch(query, {
    type: 'EQUITY',
    limit: 50,
  })

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [])

  const trimmed = query.trim()
  const showEmpty = trimmed.length === 0
  const showLoading = !showEmpty && results.length === 0 && (isLoading || isSyncing)
  const showNoMatches = !showEmpty && !showLoading && results.length === 0

  function handleSelect(symbol: string) {
    addTicker(symbol)
    onSelect(symbol)
    onClose()
  }

  function handleRemoveRecent(symbol: string, e: React.MouseEvent) {
    e.stopPropagation()
    removeTicker(symbol)
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-zinc-950 flex flex-col pt-safe pb-safe"
      role="dialog"
      aria-modal="true"
      aria-label="Search instruments"
    >
      <div className="flex items-center gap-2 px-2 h-14 border-b border-zinc-800 shrink-0">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close search"
          className="w-10 h-10 flex items-center justify-center text-zinc-300 hover:text-zinc-100"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.25"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="w-5 h-5"
            aria-hidden="true"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && results[0]) {
              e.preventDefault()
              handleSelect(results[0].instrument.symbol)
            }
          }}
          inputMode="search"
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          placeholder="Search ticker (e.g. AAPL)"
          className="flex-1 min-w-0 bg-transparent text-lg font-mono uppercase text-zinc-100 placeholder:text-zinc-600 focus:outline-none"
        />
        {query && (
          <button
            type="button"
            onClick={() => {
              setQuery('')
              inputRef.current?.focus()
            }}
            aria-label="Clear"
            className="w-10 h-10 flex items-center justify-center text-zinc-400 hover:text-zinc-200"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-5 h-5"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto overscroll-contain">
        {error && (
          <div className="px-4 py-3 text-sm text-red-400 border-b border-red-900/60 bg-red-950/30">
            {error.message}
          </div>
        )}
        {showEmpty && (
          <>
            {recentTickers.length > 0 ? (
              <ul className="divide-y divide-zinc-900">
                {recentTickers.map((symbol) => (
                  <li key={symbol}>
                    <button
                      type="button"
                      onClick={() => handleSelect(symbol)}
                      className="w-full text-left px-4 py-3.5 flex items-center justify-between gap-3 active:bg-zinc-900 hover:bg-zinc-900/60 transition"
                    >
                      <div className="font-mono text-base text-zinc-100 truncate min-w-0">
                        {symbol}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border text-zinc-400 border-zinc-700 bg-zinc-900">
                          recent
                        </span>
                        <span
                          role="button"
                          tabIndex={-1}
                          onClick={(e) => handleRemoveRecent(symbol, e)}
                          aria-label={`Remove ${symbol} from recent`}
                          className="w-7 h-7 -mr-1 flex items-center justify-center text-zinc-500 hover:text-zinc-200 text-lg leading-none"
                        >
                          ×
                        </span>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="px-6 py-10 text-center text-sm text-zinc-500">
                {isSyncing
                  ? 'Loading instruments…'
                  : 'Start typing a ticker symbol.'}
              </div>
            )}
          </>
        )}
        {showLoading && (
          <div className="px-6 py-10 text-center text-sm text-zinc-500">
            Loading instruments…
          </div>
        )}
        {showNoMatches && (
          <div className="px-6 py-10 text-center text-sm text-zinc-500">
            No matches for <span className="font-mono text-zinc-300">{trimmed.toUpperCase()}</span>.
          </div>
        )}
        {!showEmpty && results.length > 0 && (
          <ul className="divide-y divide-zinc-900">
            {results.map((r) => (
              <li key={r.instrument.symbol}>
                <button
                  type="button"
                  onClick={() => handleSelect(r.instrument.symbol)}
                  className="w-full text-left px-4 py-3.5 flex items-center justify-between gap-3 active:bg-zinc-900 hover:bg-zinc-900/60 transition"
                >
                  <div className="font-mono text-base text-zinc-100 truncate min-w-0">
                    {r.instrument.symbol}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span
                      className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border ${
                        r.trading === 'BUY_AND_SELL'
                          ? 'text-emerald-300 border-emerald-700/50 bg-emerald-900/20'
                          : 'text-zinc-400 border-zinc-700 bg-zinc-900'
                      }`}
                    >
                      {r.trading === 'BUY_AND_SELL' ? 'tradeable' : r.trading.toLowerCase()}
                    </span>
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-4 h-4 text-zinc-600"
                      aria-hidden="true"
                    >
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
